import type { Dlc, Faction, Obtained, Race, Unit, UnitKind } from "@sins2-atlas/game-data";

// The game's own display vocabulary — the words and marks the atlas uses for a Race, a
// Faction, a Class and an Obtained value. CONTEXT.md's rule is that terminology is measured
// against the game, so each table below records where its strings come from.
//
// This lives in lib/ rather than in the roster table because it is nobody's feature: seven
// modules read it, three of them (the /tech tree, the ship-item shop, the command palette)
// having nothing to do with a roster. They used to import it FROM components/roster-table,
// which meant rendering a race sigil on the research tree pulled a TanStack table module in
// with it.

// Race identity = chip with sigil (design language, confirmed 2026-07-27): TEC square amber,
// Advent circle cyan, Vasari diamond red — and since the Harbinger expansion (2.0.2.0)
// Eidolon triangle violet: the fourth distinct geometric mark, in a colour none of the
// three claimed (the game ships no Eidolon theme colour; its own gui reuses TEC art).
export const RACE_META: Record<Race, { label: string; sigil: string; textClass: string }> = {
  tec: { label: "TEC", sigil: "■", textClass: "text-tec" },
  advent: { label: "Advent", sigil: "●", textClass: "text-advent" },
  vasari: { label: "Vasari", sigil: "◆", textClass: "text-vasari" },
  eidolon: { label: "Eidolon", sigil: "▲", textClass: "text-eidolon" },
};

// The game's own class display names, verbatim from en.localized_text's unit_tag_name.*
// (verified 1.61.4.0) — including "Command Ship" for super_capital_ship.
export const UNIT_KIND_LABELS: Record<UnitKind, string> = {
  strikecraft: "Strikecraft",
  corvette: "Corvette",
  frigate: "Frigate",
  cruiser: "Cruiser",
  capital_ship: "Capital Ship",
  super_capital_ship: "Command Ship",
  titan: "Titan",
  structure: "Structure",
  starbase: "Starbase",
};

// The game's own faction names (player_faction_short_name.* / player_faction_name.* in
// en.localized_text, verified against 1.61.4.0 and 2.0.2.0). loyalist/rebel are the
// data-file ids only — Sins 1 terminology that never appears in the UI. Partial per race
// because races genuinely differ in faction count: the Eidolon field ONE faction (the game
// ids it dlc3_herald_loyalist; no rebel twin exists anywhere in the files).
export const FACTION_LABELS: Record<
  Race,
  Partial<Record<Faction, { short: string; full: string }>>
> = {
  tec: {
    loyalist: { short: "Enclave", full: "TEC Enclave" },
    rebel: { short: "Primacy", full: "TEC Primacy" },
  },
  advent: {
    loyalist: { short: "Wrath", full: "Advent Wrath" },
    rebel: { short: "Reborn", full: "Advent Reborn" },
  },
  vasari: {
    loyalist: { short: "Exodus", full: "Vasari Exodus" },
    rebel: { short: "Alliance", full: "Vasari Alliance" },
  },
  eidolon: {
    loyalist: { short: "Herald", full: "Eidolon" },
  },
};

/** The faction-EXCLUSIVITY fact: the one Faction of a Race that can have this thing — obtain
 * the unit, research the Research Subject — but only where its race HAS another Faction to be
 * excluded from. Every Eidolon unit and subject belongs to the race's single Faction, so
 * saying so on all of them would state no distinction and a one-faction race labels nothing. */
export function exclusiveFactionOf(
  race: Race,
  factions: readonly Faction[],
): { short: string; full: string } | undefined {
  const labels = FACTION_LABELS[race];
  if (Object.keys(labels).length < 2) return undefined;
  const [only, second] = factions;
  if (only === undefined || second !== undefined) return undefined;
  return labels[only];
}

/** The roster's chip form of the same fact. */
export function exclusiveFaction(
  unit: Pick<Unit, "race" | "factions">,
): { short: string; full: string } | undefined {
  return exclusiveFactionOf(unit.race, unit.factions);
}

/** The DLC chip's wording per facet value. Reinforcements names itself in the game files
 * (dlc.reinforcements.name); Harbinger ships no in-files display name, so its label is the
 * product's marketed name (Steam, 2026-09-03) — the same source the token uses. */
export const DLC_META: Record<Dlc, { title: string }> = {
  reinforcements: { title: "Requires Reinforcements DLC" },
  harbinger: { title: "Requires Harbinger DLC" },
};

/** How a player comes to own the unit — the "why isn't this in my build menu" answer, in
 * player words. ONE label per value, rendered everywhere: the filter options, the roster row
 * chip, and the entity/comparison facet cluster. `chipped` is a predicate, not a second
 * vocabulary — Build menu is the default and would repeat on 117 of 130 rows saying nothing.
 *
 * There used to be an optional `chip` shortening for dense rows, but it shortened exactly one
 * of the five values ("Auto" for Auto-built) while the other three duplicated `label`
 * verbatim — so its only effect was that the roster said "Auto" where the entity page said
 * "Auto-built" for the same fact. Measured in the live roster: the constructor-ship rows sit
 * ~74px inside the column's widest cell even with the full word, so the abbreviation bought
 * no layout at all. CONTEXT.md's Obtained glossary says "Auto-built"; that is now the only
 * spelling. A genuine width constraint can reintroduce a `shortLabel` with an explicit
 * `shortLabel ?? label` rule — earned rather than assumed. (The stat sheet's own dense
 * headers follow exactly that rule; see StatRowSpec.shortLabel in lib/stat-model.ts.) */
export const OBTAINED_META: Record<Obtained, { label: string; chipped: boolean; title: string }> = {
  direct_build: {
    label: "Build menu",
    chipped: false,
    title: "Bought from a build menu.",
  },
  hangar_built: {
    label: "Hangar",
    chipped: true,
    title: "A carrier's hangar launches it. It is not bought from a build menu.",
  },
  ability_created: {
    label: "Ability",
    chipped: true,
    title: "Another unit's ability spawns it. It is not in any build menu.",
  },
  diplomacy: {
    label: "Diplomacy",
    chipped: true,
    title: "Obtained through the diplomacy system, not a build menu.",
  },
  auto_built: {
    label: "Auto-built",
    chipped: true,
    title: "The game builds it automatically to carry out your structure orders.",
  },
};

/** How an ability's page entry says where the reader's Level leaves it (Issue 51, ADR 0012).
 * A locked ability is MARKED, never hidden: the page stays the reference it has always been,
 * showing every level's numbers and every "Requires Level" fact, and says which of them the
 * ship in front of the reader actually has. The Corruption card renders every tier the same
 * way and highlights the active one.
 *
 * The gate's badge is the GAME's phrase (user, 2026-09-16), reversing the coined "Needs
 * level N" this carried from Issue 51. The ability's own "Requires Level: 5" line does then
 * repeat it four lines below — the cost the coinage was avoiding — and that is the trade
 * taken: one word for one fact, here and in the Ability Points panel, beats a synonym the
 * game never uses. The site's own words are the two states the game has no word for. */
export const ABILITY_LEVEL_WORDS = {
  /** On an ability the hull's Level has not reached, naming the level it needs — the prefix
   * the game's own ability tooltips carry, and the one the Ability Points panel refuses a
   * gated position with (lib/frame-labels.ts, ABILITY_POINT_WORDS.gated reads this). */
  locked: (requiredLevel: number) => `Requires Level ${requiredLevel}`,
  /** On an ability the hull's Level permits and no Ability Point has been spent on (ADR
   * 0015). Ours, and CONTEXT.md says so: the game names the BUTTON ("Increase Ability
   * Level"), not the state, so there is no game word to borrow here. */
  unspent: "Unspent",
  /** On a research-levelled ability the Tech Set has not unlocked (Issue 54), naming the
   * Research Subjects it needs in the game's own words. The names are the fallback's
   * casualty: a subject this dataset cannot name leaves the bare "Needs research", which is
   * still true. */
  researchLocked: (subjectNames: readonly string[]) =>
    subjectNames.length === 0 ? "Needs research" : `Needs ${subjectNames.join(" + ")}`,
  /** Beside a multi-level ability's progression, naming the position in force. */
  inForce: (level: number) => `at level ${level}`,
  /** The title on the marked position in the "Levels 1 / 2 / 3" caption. Driver-neutral
   * since Issue 54: the level in force is the hull's Level on one ability and the reader's
   * Tech Set on the next, and the caption sits on both. */
  inForceTitle: "the ability level in force",
} as const;
